import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Heart, Loader2, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../utils/helpers';
import { ShareButton } from '../components/ShareComponents';
import MarkdownRenderer from '../components/MarkdownRenderer';
import SynastryWheel from '../components/SynastryWheel';
import AspectsTable from '../components/AspectsTable';

const PersonForm = ({ label, name, setName, date, setDate, time, setTime, place, setPlace, cities, setCities, coords, setCoords, color, searchCities, selectCity }) => (
  <div className={`glass-card p-5 rounded-xl border ${color === 'amber' ? 'border-amber-400/15' : 'border-cyan-400/15'}`}>
    <h3 className={`text-lg font-bold mb-4 ${color === 'amber' ? 'text-amber-400' : 'text-cyan-400'}`}>{label}</h3>
    <div className="space-y-3">
      <div>
        <label className="block text-xs text-[var(--text-muted)] mb-1">Nombre</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={label}
          className="mystic-input w-full" data-testid={`syn-name-${color}`} />
      </div>
      <div>
        <label className="block text-xs text-[var(--text-muted)] mb-1">Fecha de nacimiento *</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="mystic-input w-full" data-testid={`syn-date-${color}`} />
      </div>
      <div>
        <label className="block text-xs text-[var(--text-muted)] mb-1">Hora de nacimiento</label>
        <input type="time" value={time} onChange={e => setTime(e.target.value)}
          className="mystic-input w-full" data-testid={`syn-time-${color}`} />
      </div>
      <div className="relative">
        <label className="block text-xs text-[var(--text-muted)] mb-1">Ciudad de nacimiento</label>
        <input type="text" value={place}
          onChange={e => { setPlace(e.target.value); setCoords(null); searchCities(e.target.value, setCities); }}
          placeholder="Ej: Madrid" className="mystic-input w-full" data-testid={`syn-place-${color}`} />
        {cities.length > 0 && (
          <div className="absolute z-20 w-full mt-1 bg-[var(--bg-card)] border border-gold-300/10 rounded-lg shadow-xl max-h-40 overflow-y-auto">
            {cities.map((c, i) => (
              <button key={i} onClick={() => selectCity(c, setPlace, setCoords, setCities)}
                className="w-full text-left px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-white/5 transition-colors">
                {c.name}, {c.admin1 || ''}, {c.country}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

export default function SynastryPage() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const resultRef = useRef(null);

  // Person A
  const [nameA, setNameA] = useState('');
  const [dateA, setDateA] = useState('');
  const [timeA, setTimeA] = useState('12:00');
  const [placeA, setPlaceA] = useState('');
  const [citiesA, setCitiesA] = useState([]);
  const [coordsA, setCoordsA] = useState(null);

  // Person B
  const [nameB, setNameB] = useState('');
  const [dateB, setDateB] = useState('');
  const [timeB, setTimeB] = useState('12:00');
  const [placeB, setPlaceB] = useState('');
  const [citiesB, setCitiesB] = useState([]);
  const [coordsB, setCoordsB] = useState(null);

  let cityTimeout = null;
  const searchCities = (query, setter) => {
    clearTimeout(cityTimeout);
    if (query.length < 2) { setter([]); return; }
    cityTimeout = setTimeout(async () => {
      try {
        const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=5&language=es`);
        const data = await res.json();
        setter(data.results || []);
      } catch { setter([]); }
    }, 300);
  };

  const selectCity = (city, setPlace, setCoords, setCities) => {
    const label = `${city.name}, ${city.admin1 || ''}, ${city.country}`.replace(/, ,/g, ',');
    setPlace(label);
    setCoords({ lat: city.latitude, lng: city.longitude });
    setCities([]);
  };

  const streamInterpretation = async (synastryId) => {
    setIsStreaming(true);
    setStreamingText('');
    try {
      const res = await fetch(`${API}/astrology/synastry/${synastryId}/stream`, { credentials: 'include' });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) { fullText += data.text; setStreamingText(fullText); }
              if (data.done && data.analysis) { fullText = data.analysis; setStreamingText(fullText); }
              if (data.done) { setResult(prev => prev ? { ...prev, analysis: fullText } : prev); }
              if (data.error) { toast.error('Error al generar la interpretación'); }
            } catch {}
          }
        }
      }
    } catch (e) { toast.error('Error de conexión al streaming'); }
    setIsStreaming(false);
  };

  const generateSynastry = async () => {
    if (!dateA || !dateB) { toast.error('Ingresa las fechas de nacimiento de ambas personas'); return; }
    if (!user) { toast.error('Inicia sesión para generar una sinastría'); return; }
    if (user.credits < 12) { toast.error('Necesitas al menos 12 créditos para la sinastría'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API}/astrology/synastry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          person_a: { name: nameA || 'Persona A', birth_date: dateA, birth_time: timeA, birth_place: placeA, ...(coordsA || {}) },
          person_b: { name: nameB || 'Persona B', birth_date: dateB, birth_time: timeB, birth_place: placeB, ...(coordsB || {}) },
        })
      });
      if (res.status === 402) { toast.error('Créditos insuficientes. Necesitas 12 créditos.'); setLoading(false); return; }
      if (res.status === 503) { toast.error('La interpretación tardó demasiado. Inténtalo de nuevo, tus créditos no fueron descontados.'); setLoading(false); return; }
      if (!res.ok) { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Error al generar la sinastría'); setLoading(false); return; }
      const data = await res.json();
      setResult(data);
      refreshUser();
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 300);
      // Start streaming interpretation
      streamInterpretation(data.synastry_id);
    } catch (e) { toast.error('Error de conexión'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <Heart className="w-12 h-12 mx-auto mb-4 text-pink-400" />
          <h1 className="text-3xl sm:text-4xl font-bold font-heading gold-text mb-2" data-testid="synastry-title">Sinastría</h1>
          <p className="text-[var(--text-muted)] text-sm sm:text-base max-w-xl mx-auto">Descubre la compatibilidad cósmica entre dos almas. La sinastría analiza cómo los planetas de una persona interactúan con los de otra.</p>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-500/10 border border-pink-400/15 text-pink-300 text-xs">
            <Heart className="w-3 h-3" /> 12 créditos por análisis
          </div>
        </motion.div>

        {/* Form */}
        {!result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <PersonForm label="Persona A" name={nameA} setName={setNameA} date={dateA} setDate={setDateA}
                time={timeA} setTime={setTimeA} place={placeA} setPlace={setPlaceA}
                cities={citiesA} setCities={setCitiesA} coords={coordsA} setCoords={setCoordsA} color="amber"
                searchCities={searchCities} selectCity={selectCity} />
              <PersonForm label="Persona B" name={nameB} setName={setNameB} date={dateB} setDate={setDateB}
                time={timeB} setTime={setTimeB} place={placeB} setPlace={setPlaceB}
                cities={citiesB} setCities={setCitiesB} coords={coordsB} setCoords={setCoordsB} color="cyan"
                searchCities={searchCities} selectCity={selectCity} />
            </div>

            <div className="text-center">
              <button onClick={generateSynastry} disabled={loading || !dateA || !dateB}
                className="btn-gold px-8 py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="synastry-generate-btn">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Calculando compatibilidad...</> : <><Heart className="w-5 h-5 inline mr-2" />Generar Sinastría (12 créditos)</>}
              </button>
              {!user && <p className="text-[var(--text-muted)] text-xs mt-3">Inicia sesión para generar tu sinastría</p>}
            </div>
          </motion.div>
        )}

        {/* Results */}
        {result && (
          <motion.div ref={resultRef} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Header */}
            <div className="glass-card p-6 rounded-xl border border-pink-400/15 text-center">
              <h2 className="text-2xl font-bold text-pink-300 mb-1" data-testid="synastry-result-title">
                {result.person_a?.name} ♡ {result.person_b?.name}
              </h2>
              <p className="text-[var(--text-muted)] text-sm">
                {result.person_a?.sun_sign} × {result.person_b?.sun_sign}
              </p>
              <div className="flex justify-center gap-4 mt-4">
                <ShareButton
                  type="synastry"
                  content={{
                    person_a: result.person_a,
                    person_b: result.person_b,
                    cross_aspects: result.cross_aspects,
                    analysis: result.analysis,
                  }}
                  title={`Sinastría: ${result.person_a?.name} ♡ ${result.person_b?.name}`}
                  description={`Compatibilidad cósmica entre ${result.person_a?.sun_sign} y ${result.person_b?.sun_sign}`}
                />
                <button onClick={() => { setResult(null); setStreamingText(''); }} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 text-[var(--text-muted)] hover:bg-white/10 transition-all text-sm" data-testid="synastry-new-btn">
                  Nueva Sinastría
                </button>
              </div>
            </div>

            {/* Bi-Wheel */}
            <div className="glass-card p-6 rounded-xl border border-gold-300/10">
              <h3 className="text-lg font-bold text-purple-300 mb-4 text-center">Rueda de Sinastría</h3>
              <SynastryWheel
                chartDataA={result.person_a?.chart_data}
                chartDataB={result.person_b?.chart_data}
                crossAspects={result.cross_aspects}
                nameA={result.person_a?.name}
                nameB={result.person_b?.name}
              />
            </div>

            {/* Planetary Positions Side by Side */}
            <div className="grid md:grid-cols-2 gap-4">
              {[['person_a', 'amber'], ['person_b', 'cyan']].map(([key, color]) => {
                const person = result[key];
                return (
                  <div key={key} className={`glass-card p-5 rounded-xl border ${color === 'amber' ? 'border-amber-400/10' : 'border-cyan-400/10'}`}>
                    <h4 className={`text-base font-bold mb-3 ${color === 'amber' ? 'text-amber-400' : 'text-cyan-400'}`}>
                      {person?.name} — {person?.sun_sign}
                    </h4>
                    <div className="space-y-1">
                      {person?.planets?.map((p, i) => (
                        <div key={i} className="flex justify-between items-center text-xs py-1 border-b border-white/5 last:border-0">
                          <span className="text-[var(--text-secondary)]">{p.name_es || p.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{p.sign}</span>
                            <span className="text-purple-400 text-[10px]">{p.position}°</span>
                            {p.retrograde && <span className="text-red-400 text-[10px] font-bold">R</span>}
                            {p.house > 0 && <span className="text-[var(--text-muted)] text-[10px]">Casa {p.house}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Cross Aspects Table */}
            {result.cross_aspects?.length > 0 && (
              <div className="glass-card p-6 rounded-xl border border-gold-300/10">
                <AspectsTable aspects={result.cross_aspects} />
              </div>
            )}

            {/* AI Interpretation - Streaming */}
            <div className="glass-card p-6 sm:p-8 rounded-xl border border-gold-300/10">
              <h3 className="text-xl font-bold text-purple-300 mb-4">
                Análisis de Compatibilidad
                {isStreaming && <span className="ml-2 inline-block w-2 h-2 bg-purple-400 rounded-full animate-pulse" />}
              </h3>
              {(streamingText || result.analysis) ? (
                <MarkdownRenderer content={streamingText || result.analysis} className="text-[var(--text-secondary)] text-sm leading-relaxed" />
              ) : isStreaming ? (
                <div className="flex items-center gap-3 text-[var(--text-muted)] text-sm py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                  Generando interpretación cósmica...
                </div>
              ) : null}
            </div>

            <p className="text-center text-[var(--text-muted)] text-xs">Créditos utilizados: {result.credits_used}</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
